/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import {
    bool, func, object, string,
} from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

// Material UI
import { Button, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import CloseCalendarConsent from '../CloseCalendarConsent/CloseCalendarConsent';

import { calendarColorStyle } from '../../../Helpers/Style-Helper';
import { getFormattedDate } from '../../../Helpers/Helper';

import { getFeatureFlags } from '../../../../../../../../../state/ducks/MfeCheckout/ducks/App/App-Selectors';
import { updateOrder } from '../../../../../../../../../state/ducks/MfeCheckout/ducks/Cart/ducks/UpdateCart/UpdateCart-Actions';
import { trackEvent } from '../../../../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';

const useStyles = makeStyles(
    (theme) => ({
        buttonContainer: {
            padding: '0 48px 24px 0',
            [theme.breakpoints.down(490)]: {
                padding: '0 0 16px',
                flexGrow: 0,
                maxWidth: '100%',
                flexBasis: '100%',
            },
        },
        buttonContWide: {
            padding: '0 38px 24px',
            [theme.breakpoints.down(490)]: {
                padding: '0 16px 16px',
            },
        },
        saveDateButton: {
            backgroundColor: theme.palette.buttonBackgroundColor,
            color: calendarColorStyle.common.white,
            margin: '0',
            padding: '11px 24px',
            textTransform: 'capitalize',
            fontWeight: '700',
            fontSize: '1em',
            lineHeight: '1.313em',
            width: '131px',
            boxShadow: 'none',
            [theme.breakpoints.down(490)]: {
                width: 'calc(100% - 32px)',
                padding: '15px 28px',
                position: 'absolute',
                bottom: '16px',
                margin: '0 16px',
            },
            '&:hover, &:focus, &:active': {
                backgroundColor: theme.palette.buttonBackgroundColorSecondary,
            },
        },
        saveDateButtonWide: {
            width: '100%',
            [theme.breakpoints.down(490)]: {
                width: 'calc(100% - 32px)',
                margin: 0,
            },
        },
    }),
    { name: 'SaveDateButton' },
);

const SaveDateButton = ({
    calendarData,
    flexOptionSelected,
    forceExactDay,
    handleDeliveryCalendarClose,
    holidaySelected,
    isOpenCalendarConsent,
    orderItem,
    pageType,
    selectedDeliveryDateObj,
}) => {
    const dispatchAction = useDispatch();
    const classes = useStyles();
    const featureFlags = useSelector(getFeatureFlags);
    const {
        calendarDateType,
        deliveryDate,
        deliverySLA,
        flexDetails,
        holidayCode,
        holidayStartDate,
        productSku,
        rangeStartDate,
        upcharge,
        surcharge,
        weekOfCode,
    } = selectedDeliveryDateObj;

    const totalCharges = (parseFloat(upcharge || 0, 10)) + (parseFloat(surcharge || 0, 10));

    let sizeColumn = 5;
    let classRoot = '';
    let classButton = '';
    if (
        ['WO', 'MO'].includes(calendarData.deliveryIndicator)
        || selectedDeliveryDateObj?.flexDetails?.option?.length > 0
    ) {
        sizeColumn = 12;
        classRoot = classes.buttonContWide;
        classButton = classes.saveDateButtonWide;
    }
    const updateDeliveryDate = () => {
        let delSLA = deliverySLA;
        let calDateType = calendarDateType;
        let calStartDate = rangeStartDate;
        let updateHolidayCode;
        let selectedHolidaycode = '';

        if (holidayCode && Array.isArray(holidayCode)) {
            selectedHolidaycode = (holidayCode.includes(holidaySelected)) ? holidaySelected : '';
        } else {
            selectedHolidaycode = holidayCode;
        }

        if (holidaySelected && holidaySelected !== 'Holiday Delivery') {
            if (holidayStartDate && selectedHolidaycode) {
                calStartDate = holidayStartDate;
            }
            updateHolidayCode = holidaySelected;
        } else if (weekOfCode) {
            updateHolidayCode = weekOfCode;
        } else {
            updateHolidayCode = '';
        }

        if (forceExactDay && calDateType === 'between') {
            delSLA = calendarData.guaranteedMethod;
            calDateType = 'on';
            calStartDate = '';
        }

        const formattedDeliveryDate = getFormattedDate(deliveryDate, 'MM/dd/yyyy HH:mm:ss');

        let calStartDateValue = '';
        if (calStartDate) {
            calStartDateValue = getFormattedDate(calStartDate, 'MM/dd/yyyy HH:mm:ss');
        }

        const reqObj = {
            updateDeliveryDate: {
                backupProductCode: '',
                calendarDateType: calDateType,
                calendarStartDate: calStartDateValue,
                deliveryDate: formattedDeliveryDate,
                deliverySLA: delSLA,
                deliveryTimeWindow: '',
                flex: {},
                holidayCode: updateHolidayCode,
                locationType: '',
                useDateForAll: '',
                zipCode: '',
            },
            quantity: null,
            orderItemId: orderItem.cartItemId,
        };

        const flexVariantId = flexDetails?.variantId;
        const flexOptionKey = flexOptionSelected?.optionKey;
        const flexOptionId = flexOptionSelected?.optionCode;
        const forcedDate = flexOptionSelected?.forcedDelDate;

        if (flexOptionKey && flexOptionId) {
            reqObj.updateDeliveryDate.flex = {
                flexVariantId,
                flexOptionKey,
                flexOptionId,
            };
        }

        if (flexDetails?.forceFlex === 'true' && featureFlags['is-new-flex-flows-enabled']) {
            reqObj.updateDeliveryDate.deliveryDate = getFormattedDate(forcedDate, 'MM/dd/yyyy HH:mm:ss');
        }

        const updateOrderProps = {
            page: pageType,
            updateType: 'delivery',
        };

        dispatchAction(updateOrder(
            reqObj, updateOrderProps, handleDeliveryCalendarClose,
        ));
    };

    const handleSaveDate = () => {
        let calendarDateChanged = false;
        const curSelectedDate = selectedDeliveryDateObj?.deliveryDate || '';
        const currentSelectedFromCart = orderItem.deliveryInfo?.deliveryDate;

        if (forceExactDay && selectedDeliveryDateObj.calendarDateType !== 'on') {
            calendarDateChanged = true;
        } else if (curSelectedDate && curSelectedDate !== '' && currentSelectedFromCart !== curSelectedDate) {
            calendarDateChanged = true;
        } else if (
            holidaySelected !== ''
            && holidaySelected !== 'Holiday Delivery'
            && orderItem?.deliveryInfo?.holidayCode !== holidaySelected
        ) {
            calendarDateChanged = true;
        }

        if (calendarDateChanged) {
            updateDeliveryDate();
        } else {
            handleDeliveryCalendarClose();
        }

        dispatchAction(trackEvent({
            eventName: 'feature_interaction',
            eventCategory: 'Checkout',
            eventAction: 'Calendar',
            eventLabel: 'Date CTA',
        }));

        dispatchAction(trackEvent({
            eventName: 'feature_interaction',
            eventCategory: 'Checkout',
            eventAction: 'Enterprise Calendar',
            eventLabel: `${deliveryDate} | ${productSku}`,
        }));

        if (totalCharges > 0) {
            dispatchAction(trackEvent({
                eventName: 'feature_interaction',
                eventCategory: 'Checkout',
                eventAction: 'Enterprise Calendar',
                eventLabel: `Surcharge: $${totalCharges.toFixed(2)} | ${productSku}`,
            }));
        }
    };

    return (
        <Grid
            item
            xs={sizeColumn}
            className={`${classes.buttonContainer} ${classRoot}`}
        >
            {
                isOpenCalendarConsent
                && (
                    <CloseCalendarConsent
                        closeModal={handleDeliveryCalendarClose}
                        saveDate={handleSaveDate}
                        closeAreYouSureMessage={isOpenCalendarConsent}
                    />
                )
            }
            <Grid id="apply-button" container justifyContent="flex-end">
                <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    onClick={handleSaveDate}
                    className={`${classes.saveDateButton} ${classButton}`}
                    data-test="checkout-delivery-calendar-apply-date"
                >
                    Save Date
                </Button>
            </Grid>
        </Grid>
    );
};

SaveDateButton.propTypes = {
    calendarData: object.isRequired,
    flexOptionSelected: object,
    forceExactDay: bool.isRequired,
    handleDeliveryCalendarClose: func.isRequired,
    holidaySelected: string.isRequired,
    isOpenCalendarConsent: bool.isRequired,
    orderItem: object.isRequired,
    pageType: string.isRequired,
    selectedDeliveryDateObj: object.isRequired,
};

SaveDateButton.defaultProps = {
    flexOptionSelected: null,
};

export default SaveDateButton;
